import { useCallback, useEffect, useState } from 'react';
import { api, ErreurApi, type Releve } from './api';
import { PlanDeTravail } from './plan-travail';
import { Icone } from './icones';
import { MesAffaires } from './mes-affaires';

/**
 * L'ÉCRAN D'ARRIVÉE DU GÉOMÈTRE — §37 à §43.
 *
 * ⚠️ RÈGLE DU FONDATEUR, 21/09/2026 : « on ne peut pas être dans l'uniformité,
 * car chaque mode est un métier avec ses propres exigences, ses propres flux,
 * ses propres attentes — chacun sera un OS. »
 *
 * Les sept métiers partageaient un seul écran d'arrivée, `plan`, qui rend les
 * mêmes tuiles pour tout le monde. Les COMPTEURS étaient bien différenciés — le
 * service les tient par métier — mais la PRÉSENTATION ne l'était pas : un
 * géomètre et un fiscaliste arrivaient sur la même grille, et devaient y
 * chercher leur propre travail.
 *
 * ⚠️ CE QUI REND CET ÉCRAN MÉTIER, ET NON UNE COPIE DU BUREAU DU NOTAIRE : le
 * notaire est groupé par ACTE (ce qu'il doit faire signer) ; le géomètre est
 * groupé par **ce que chaque levé ATTEND**. Ce n'est pas la même question, et
 * ce n'est pas le même geste :
 *
 *   · une anomalie ouverte attend LUI — il corrige ;
 *   · un levé déposé sans décision attend QUELQU'UN D'AUTRE — il patiente ;
 *   · un levé décidé n'attend plus personne.
 *
 * Grouper par `statut` aurait redonné une liste uniforme : `depose`, `valide`,
 * `rejete` sont des mots du registre, pas des files de travail. Un écran qui
 * trie par statut oblige à savoir d'avance lequel appelle un geste.
 */

/** Ce qu'un levé attend — la seule question qui organise l'écran du géomètre. */
type Attente = 'moi' | 'autrui' | 'personne';

function attenteDe(r: Releve): Attente {
  if ((r.anomalies_ouvertes ?? 0) > 0) return 'moi';
  if (r.decide_le) return 'personne';
  return 'autrui';
}

const FILES: Array<{ cle: Attente; titre: string; sous: string }> = [
  { cle: 'moi', titre: 'À corriger', sous: 'une anomalie est ouverte : elle empêche la décision, et c’est à vous de la lever' },
  { cle: 'autrui', titre: 'En attente de décision', sous: 'déposé et contrôlé — la validation ne vous appartient pas' },
  { cle: 'personne', titre: 'Décidés', sous: 'la décision est prise ; le levé a produit son effet ou il a été rejeté' },
];

export function BureauGeometre(
  { modeCode, onOuvrirRubrique, onOuvrirAffaire, onReprendreSaisie }:
  {
    modeCode: string; onOuvrirRubrique: (rubrique: string) => void;
    /** ⚠️ Le géomètre aussi tient des affaires : un levé s'inscrit dans une
     *  procédure, et `fon.affaire_acteur` l'y rattache depuis `releves.ts:159`. */
    onOuvrirAffaire: (reference: string) => void;
    onReprendreSaisie?: (typeCode: string, parcelle: string | null) => void;
  },
) {
  const [releves, setReleves] = useState<Releve[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  const charger = useCallback(async () => {
    setErreur(null);
    try { setReleves((await api.releves.liste()).releves ?? []); }
    catch (e) { setErreur(e instanceof ErreurApi ? e.message : String(e)); setReleves(null); }
  }, []);
  useEffect(() => { void charger(); }, [charger]);

  return (
    <div className="bureau">
      {/* Le FLUX : tout ce qui se lit. Le rail d'opérations lui est frère,
          jamais un élément de grille qui traverse ses rangées — c'est ce qui
          fabriquait une bande morte sous le titre. */}
      <div className="bureau-flux">
      <header className="bureau-tete">
        <div>
          <h2><Icone nom="geometre" taille={20} /> Bureau du géomètre</h2>
          <p className="mention">
            Un levé n’est pas un document : c’est ce qui rend une parcelle OPPOSABLE.
            Tant qu’il porte une anomalie déterminante, la décision est impossible —
            et la parcelle reste présomptive.
          </p>
        </div>
      </header>

      {/* ⚠️ MÊME PORTE QUE LES AUTRES BUREAUX : un écran d'arrivée sans
          affaires oblige l'agent à deviner où vit son travail. */}
      <MesAffaires onOuvrirAffaire={onOuvrirAffaire} onReprendreSaisie={onReprendreSaisie} />

      {/* ⚠️ LES TUILES DU MÉTIER, PAS CELLES DE TOUT LE MONDE. `PlanDeTravail`
          mesure les compteurs DU MODE — pour le géomètre : ses travaux, les
          levés à décider, les parcelles présomptives. On réutilise le composant
          au lieu d'en recopier les tuiles : deux implémentations d'une même
          règle divergent au premier correctif appliqué à une seule. */}
      <PlanDeTravail modeCode={modeCode} onOuvrirRubrique={onOuvrirRubrique} encastre />

      <section className="bureau-files">
        <h3>Vos levés, par ce qu’ils attendent</h3>
        {erreur && <p className="erreur" role="alert">{erreur}</p>}
        {!releves && !erreur && <p className="attente">Lecture de vos levés…</p>}

        {/* ⚠️ « AUCUN LEVÉ » ET « JE N'AI PAS PU REGARDER » NE SONT PAS LA MÊME
            CHOSE, et se ressemblent trait pour trait à l'écran. La liste vide
            ne s'affiche que si la lecture a RÉUSSI. */}
        {releves && releves.length === 0 && (
          <p className="indetermine">
            Aucun levé à votre nom. Ce n’est pas « rien à faire » : c’est « vous n’en avez déposé aucun ».
          </p>
        )}

        {releves && releves.length > 0 && FILES.map((f) => {
          const dedans = releves.filter((r) => attenteDe(r) === f.cle);
          return (
            <div key={f.cle} className={`file file-${f.cle}`}>
              <div className="file-tete">
                <strong>{f.titre}</strong>
                {/* ⚠️ LA POPULATION, TOUJOURS — « 2 sur 14 », jamais « 2 ».
                    Un nombre nu ne dit pas si un zéro est une bonne nouvelle
                    ou une file qu'on n'a pas alimentée. */}
                <span className="jeton">{dedans.length} sur {releves.length}</span>
                <span className="aide">{f.sous}</span>
              </div>
              {dedans.length === 0
                ? <p className="file-vide">Rien dans cette file.</p>
                : (
                  <ul className="file-liste">
                    {dedans.map((r) => (
                      <li key={r.reference}>
                        <button type="button" className="lien" onClick={() => onOuvrirRubrique('geometre')}>
                          {r.reference}
                        </button>
                        <span className="jeton">{r.methode}</span>
                        <span className="aide">± {r.precision_annoncee_m} m</span>
                        {r.surface_m2 != null && (
                          <span className="aide">{Number(r.surface_m2).toLocaleString('fr-FR')} m²</span>
                        )}
                        {(r.anomalies_ouvertes ?? 0) > 0 && (
                          <span className="jeton rouge">
                            {r.anomalies_ouvertes} anomalie{(r.anomalies_ouvertes ?? 0) > 1 ? 's' : ''}
                          </span>
                        )}
                        {r.parcelle
                          ? <span className="jeton vert">{r.parcelle}</span>
                          : <span className="aide">aucune parcelle produite</span>}
                      </li>
                    ))}
                  </ul>
                )}
            </div>
          );
        })}
      </section>

      </div>

      <section className="bureau-intentions">
        <h3>Ouvrir une opération</h3>
        <div className="bureau-boutons">
          {/* ⚠️ LES GESTES DU GÉOMÈTRE, PAS CEUX DU NOTAIRE. Il ne dresse pas
              d'acte et n'ouvre pas de succession : il lève, il borne, il fait
              entendre les riverains. Un écran qui lui proposerait « déposer un
              acte de vente » lui dirait que cet OS n'est pas le sien. */}
          <button type="button" className="bouton discret" onClick={() => onOuvrirRubrique('geometre')}>
            + Déposer un levé
          </button>
          <button type="button" className="bouton discret" onClick={() => onOuvrirRubrique('geometre')}>
            + Recueillir les riverains
          </button>
          <button type="button" className="bouton discret" onClick={() => onOuvrirRubrique('anomalies')}>
            + Traiter une anomalie
          </button>
          {/* ⚠️ LA RUBRIQUE VISÉE A CHANGÉ DE NOM LE 22/09/2026 : le plan
              cadastral est entré dans la carte. Ce bouton pointait encore sur
              « assemblage », une clé que le catalogue ne connaît plus — le rail
              l'aurait affichée « déclarée et inconnue de cette interface »,
              c'est-à-dire un cul-de-sac silencieux depuis un cockpit de
              métier. C'est le motif « corriger une occurrence ⇒ grep du
              motif » : le `grep` l'a trouvé, pas la relecture. */}
          <button type="button" className="bouton discret" onClick={() => onOuvrirRubrique('carte')}>
            + Assembler un plan
          </button>
        </div>
      </section>
    </div>
  );
}
